import React from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const GoalCard = ({ goal, onEdit, onDelete, onViewDetails }) => {
  const getGoalIcon = (type) => {
    const icons = {
      retirement: 'PiggyBank',
      car: 'Car',
      home: 'Home',
      education: 'GraduationCap',
      wedding: 'Heart',
      custom: 'Target'
    };
    return icons?.[type] || 'Target';
  };

  const getGoalColor = (type) => {
    const colors = {
      retirement: 'bg-indigo-500',
      car: 'bg-blue-500',
      home: 'bg-emerald-500',
      education: 'bg-purple-500',
      wedding: 'bg-pink-500',
      custom: 'bg-amber-500'
    };
    return colors?.[type] || 'bg-gray-500';
  };

  const formatIndianCurrency = (amount) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      maximumFractionDigits: 0
    })?.format(amount);
  };

  const calculateProgress = () => {
    return Math.min((goal?.currentSavings / goal?.targetAmount) * 100, 100);
  };

  const calculateMonthsRemaining = () => {
    const targetDate = new Date(goal.targetDate);
    const today = new Date();
    const months = (targetDate?.getFullYear() - today?.getFullYear()) * 12 + 
                   (targetDate?.getMonth() - today?.getMonth());
    return Math.max(0, months);
  };

  const progress = calculateProgress();
  const monthsRemaining = calculateMonthsRemaining();

  return (
    <div className="bg-card rounded-xl border border-border p-6 hover:shadow-lg transition-all duration-300">
      <div className="flex items-start justify-between mb-4">
        <div className="flex items-center gap-3">
          <div className={`${getGoalColor(goal?.type)} p-3 rounded-lg`}>
            <Icon name={getGoalIcon(goal?.type)} size={24} color="white" />
          </div>
          <div>
            <h3 className="text-lg font-semibold text-foreground">{goal?.name}</h3>
            <p className="text-sm text-muted-foreground capitalize">{goal?.type} Goal</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={() => onEdit(goal)}
            className="p-2 hover:bg-muted rounded-lg transition-colors focus-ring"
            aria-label="Edit goal"
          >
            <Icon name="Edit2" size={18} color="var(--color-muted-foreground)" />
          </button>
          <button
            onClick={() => onDelete(goal?.id)}
            className="p-2 hover:bg-destructive/10 rounded-lg transition-colors focus-ring"
            aria-label="Delete goal"
          >
            <Icon name="Trash2" size={18} color="var(--color-destructive)" />
          </button>
        </div>
      </div>
      <div className="space-y-4">
        <div className="flex items-center justify-between">
          <span className="text-sm text-muted-foreground">Target Amount</span>
          <span className="text-xl font-bold text-foreground">{formatIndianCurrency(goal?.targetAmount)}</span>
        </div>

        <div className="flex items-center justify-between">
          <span className="text-sm text-muted-foreground">Current Savings</span>
          <span className="text-lg font-semibold text-accent">{formatIndianCurrency(goal?.currentSavings)}</span>
        </div>

        <div>
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm text-muted-foreground">Progress</span>
            <span className="text-sm font-medium text-foreground">{progress?.toFixed(1)}%</span>
          </div>
          <div className="w-full bg-muted rounded-full h-2.5">
            <div
              className={`${getGoalColor(goal?.type)} h-2.5 rounded-full transition-all duration-500`}
              style={{ width: `${progress}%` }}
            />
          </div>
        </div>

        <div className="grid grid-cols-2 gap-4 pt-4 border-t border-border">
          <div>
            <p className="text-xs text-muted-foreground mb-1">Target Date</p>
            <p className="text-sm font-medium text-foreground">{new Date(goal.targetDate)?.toLocaleDateString('en-IN', { month: 'short', year: 'numeric' })}</p>
          </div>
          <div>
            <p className="text-xs text-muted-foreground mb-1">Time Remaining</p>
            <p className="text-sm font-medium text-foreground">{monthsRemaining} months</p>
          </div>
        </div>

        <div className="bg-muted/50 rounded-lg p-3">
          <div className="flex items-center justify-between">
            <span className="text-sm text-muted-foreground">Monthly Savings Required</span>
            <span className="text-base font-bold text-primary">{formatIndianCurrency(goal?.monthlySavingsRequired)}</span>
          </div>
        </div>

        <Button
          variant="outline"
          fullWidth
          iconName="TrendingUp"
          iconPosition="left"
          onClick={() => onViewDetails(goal)}
        >
          View Details
        </Button>
      </div>
    </div>
  );
};

export default GoalCard;